import CommonApi from '@/pages/api'
import { ProfileEndpoint, QuestionAnswerEndpoint, RolesEndpoints } from '@/pages/api/endpoints'
import type { AlertColor } from '@mui/material'
import { create } from 'zustand'
import Cookies from 'js-cookie'
import { sanitizeExternalUrl } from '@/src/utils/safeUrl'
import { clearAnalyticsFilters } from '@/src/utils/analyticsFilterStorage'

interface RoleData {
  id: number
  name: string
}
interface ListData {
  description: string
  listName: string
}
interface statsType {
  list_id?: number | null
  list_name?: string
  status?: string
  description?: string
  updated?: null
  created?: null
  total?: number
  active?: number
  suppressed?: number
  unsubscribed?: number
  type?: string
}
interface QuestionDataType {
  id: number
  status: string
  created_on: string
  answer_date: string
  email: string
  business_name: string
  public: boolean
  content: { headline: string; id: number }
  user: string
  first_name: string
  last_name: string
  question: string
  answer: string
  logo_mark: string
}

interface ListFormDistributionData {
  description: string
  listName: string
}

interface ThemeColorType {
  primary: string
  secondary: string
  email_bg_color: string
}
interface TextColorType {
  primaryTextDark: boolean
  secondaryTextDark: boolean
}
interface ThemeLogoTypes {
  default_logo: string
  logo_mark: string
  dark_logo_mark: string
  logo: string
  dark_logo: string
  logo_mark_file_name: string
  dark_logo_mark_file_name: string
  logo_file_name: string
  dark_logo_file_name: string
  logo_mark_size: string
  dark_logo_mark_size: string
  logo_size: string
  dark_logo_size: string
}
export type HomeLinkBehaviour = 'new_tab' | 'same_tab'

export interface LoginBannerSettings {
  position_x: number
  position_y: number
  zoom: number
  overlay: boolean
  overlay_strength: 'light' | 'medium' | 'strong'
}

// GET /theme `login_banner`. null = the Relait default banner on the auth pages.
export interface LoginBanner extends LoginBannerSettings {
  key: string
  url: string
  filename: string
  uploaded_at: string
}

export interface HomePageTheme {
  banner: string
  banner_file_name: string
  login_banner?: LoginBanner | null
  title: string
  text: string
  code: string
  primary_domain: string
  notification_stream?: boolean
  announcement_stream?: boolean
  show_new_dashboard?: boolean | null
  active_dashboards?: string[]
  share_registry: string
  // Gates the Post-Raise Analysis feature (Capital Events tile + page). Set
  // per tenant by Relait staff; defaults true for Automic/XCend tenancies.
  enable_placements?: boolean
  // Gates the Post-IPO Analysis feature (Capital Events tile + page). Set per
  // tenant by Relait staff for companies with a tracked IPO.
  enable_post_ipo?: boolean
  // Project Boston tier. Undefined only while /theme is loading or from an
  // older cached response; treat as 'relait_360' (full product, IC served).
  // Legacy 'premium' never appears here: /theme resolves the plan through
  // the tier profile catalogue, which maps premium/unknown to relait_360
  // before emitting. (The portal's SubscriptionStore reads the raw column
  // and CAN see 'premium'; that store is typed separately.)
  plan?: 'essentials' | 'intelligence' | 'relait_360'
  // 'investor_centre' (360) or 'landing_page' (Essentials/Intelligence).
  public_surface?: 'investor_centre' | 'landing_page'
  // Whether the public signup form may register new contacts. False on
  // Intelligence, true on Essentials and 360. Undefined = older cached
  // response; treat as true (see useTier).
  widget_signup?: boolean
  // Whether investor accounts can log in. True only on 360. Undefined =
  // older cached response; treat as true (see useTier).
  investor_login?: boolean
  // "Visit our website" target on the non-360 landing page.
  corporate_home_url?: string | null
  // Computershare tenants that receive an ongoing daily transaction feed have
  // this set true. Registry-export-only Computershare tenants (holders only, no
  // transactions) are false, and activity/movement surfaces degrade for them.
  has_transaction_feed?: boolean
  business_name: string
  enable_email_logo: boolean
  show_short_summaries?: boolean
  auto_pin_presentation?: boolean
  send_auto_email?: boolean
  central_banner?: {
    button_url: string | null | undefined
    button_label: string | null | undefined
    message: string
    alert_type: 'error' | 'warning' | 'info'
    updated_on: string
  } | null
  segment_ids?: string[] | null
  ps_header?: string | null
  ps_salutation?: string | null
  ps_footer?: string | null
  custom_site_url?: string | null
  custom_site_announcements_url?: string | null
  investor_centre_url?: string | null
  is_custom_site?: boolean
  hide_from_google?: boolean
  hide_daily_report?: boolean
  send_daily_report_email?: boolean
  news_search_terms?: string[]
  // Controls whether the investor-portal logo / home button open the company
  // website in a new tab or the same one. Undefined (older cached /theme
  // responses) is treated as 'new_tab' — the long-standing behaviour.
  home_link_behaviour?: HomeLinkBehaviour
  youtube_sync_enabled?: boolean
  youtube_channel_url?: string | null
  youtube_source_title?: string | null
  youtube_auto_publish?: boolean
  youtube_backfill_count?: number
  youtube_last_synced_at?: string | null
  auto_show_latest_videos?: boolean
}

// A Computershare or MUFG tenant that does NOT receive an ongoing daily
// transaction feed: it has holders but no transaction/movement data, so activity
// and movement surfaces must be hidden. MUFG has no API - the registry drops a
// daily CSV into S3 - so a freshly converted MUFG tenant has no movements until
// the first file is ingested. Automic, XCend, and Computershare/MUFG tenants
// that do have a feed are unaffected. We only hide when has_transaction_feed
// is explicitly false: an undefined flag (older cached /theme responses, or the
// window where the frontend ships ahead of the backend) keeps the pre-flag
// behaviour so a feed-having CS tenant is never wrongly stripped of surfaces.
export const isCsNoFeed = (homeTheme?: Pick<HomePageTheme, 'share_registry' | 'has_transaction_feed'> | null): boolean =>
  ['computer_share', 'mufg'].includes(homeTheme?.share_registry ?? '') &&
  homeTheme?.has_transaction_feed === false

// A Webby-built site is live for the tenant: the custom-site toggle is on AND a
// safe Website URL is set. The toggle alone is not enough, it is switched on first.
export const customSiteUrl = (homeTheme?: HomePageTheme | null): string | null =>
  homeTheme?.is_custom_site ? sanitizeExternalUrl(homeTheme.custom_site_url) : null

const CUSTOM_SITE_PATHS: Record<string, string> = {
  '/news-insights': '/investor-centre/in-the-news',
  '/research': '/investor-centre/research',
  '/corporate': '/corporate/corporate-governance'
}

// Where an IC path lands on the Webby site, so a visitor arrives at the matching page
// rather than the homepage. Announcements falls back to the homepage until its URL is set.
export const customSiteRedirectUrl = (
  homeTheme: HomePageTheme | null | undefined,
  pathname: string
): string | null => {
  const siteUrl = customSiteUrl(homeTheme)
  if (!siteUrl) return null
  if (pathname === '/announcements') {
    return sanitizeExternalUrl(homeTheme?.custom_site_announcements_url) ?? siteUrl
  }
  const path = CUSTOM_SITE_PATHS[pathname]
  if (!path) return siteUrl
  const url = new URL(siteUrl)
  url.pathname = `${url.pathname.replace(/\/+$/, '')}${path}`
  url.search = ''
  url.hash = ''
  return url.toString()
}

interface DistributionListDetails {
  list_id: number
  list_name: string
  status: string
  description: string
  updated: null
  created: null
  total_contacts: number
  active: number
  suppressed: number
  unsubscribed: number
  id: number
  full_name: string
  email: string
  total: number
  next?: boolean
  type?: string
}
interface DistributionUserDetails {
  user_details: Array<{
    id: number
    full_name: string
    first_name: string
    last_name: string
    email: string
    status: string
  }>
  total: number
}
interface AllDistributionUserDetails {
  user_details?: Array<{
    id: number
    full_name: string
    first_name: string
    last_name: string
    email: string
    status: string
  }>
  total?: number
}
interface ReviewListItem {
  id: number
  first_name: string
  last_name: string
  email: string
  status: string
}

type ReviewListDetails = ReviewListItem[]

interface ThumbnailItem {
  id: number
  filename: string
  url: string
}

export type ThumbnailDetails = ThumbnailItem[]

interface CartStore {
  role: RoleData[]
  annnoucementPage: number
  themeLoading: boolean
  themeColours: ThemeColorType
  TextColorType: TextColorType
  themeLogo: ThemeLogoTypes
  homeTheme: HomePageTheme
  profileLoading: boolean
  showSidebar: boolean
  profile: {
    business_name: string
    first_name: string
    last_name: string
    email: string
    contact: string
    user_type: string
    id: number | string
    address_data: {
      city: string
      state: string
      street: string
      postcode: string
      country: string
    }
    admin_mail: string
    service_email: string
    role_data: {
      name: string | null
      id: number | null
    }
  }
  permissions: Array<{
    id: number
    name: string
    read: boolean
    write: boolean
  }>
  permissionsLoaded: boolean
  ContentPdf: {
    file: string
    file_data: {
      name: string
      size: string
    }
  }
  UploadFile: any
  UploadContentUrl: string
  uploadThread: string
  redirectionUrl: string
  annYearsList: string[]
  statsData: any
  adminQuestion: QuestionDataType
  initialAdminQuestion: QuestionDataType
  questionTableData: {
    results: QuestionDataType[]
    total: number
  }
  wantEdit: boolean
  questionEditModal: boolean
  distributionListDetails: DistributionListDetails
  distributionUserDetails: DistributionUserDetails
  allDistributionUserDetails: AllDistributionUserDetails
  selectedContacts: DistributionUserDetails
  reviewListDetails: ReviewListDetails
  companyThumbnails: ThumbnailDetails
  listFormDistributionData: ListFormDistributionData
  listData: ListData
  listStats?: statsType
  statsLoading: boolean
  toastOpen: boolean
  toastMessage: string
  toastType: AlertColor
  toastDuration: number

  setWantEdit: (data: boolean) => void
  setContentPdf: (data: any) => void
  setAdminQuestion: (data: any) => void
  setProfileLoading: (data: any) => void
  setShowSidebar: (data: boolean) => void
  setProfile: (data: any) => void
  setQuestionEditModal: (data: any) => void
  fetchAllQuestion: (query: string, currPage: number, pageLimit: number, status?: string) => void
  fetchQuestionById: (id: number) => any
  fetchProfile: () => void
  fetchPermissions: () => void
  fetchRole: () => void
  setInitialQuestionChanges: (data: any) => void
  setThemeColours: (data: ThemeColorType) => void
  setTextColorType: (data: TextColorType) => void
  setThemeLogos: (data: ThemeLogoTypes) => void
  setHomeTheme: (data: Partial<HomePageTheme>) => void
  setUploadFile: (data: File) => void
  setUploadContentUrl: (data: string) => void
  setUploadThread: (data: string) => void
  setRedirectionUrl: (data: string) => void
  setAnnYearsList: (data: string[]) => void
  setStatsData: (data: any) => void
  setAnnoucementPage: (data: number) => void
  setThemeLoading: (data: boolean) => void
  setDistributionListDetails: (data: DistributionListDetails) => void
  setDistributionUserDetails: (data: DistributionUserDetails) => void
  setAllDistributionUserDetails: (data: AllDistributionUserDetails) => void
  setSelectedContacts: (data: DistributionUserDetails) => void
  setReviewListDetails: (data: ReviewListDetails) => void
  setCompanyThumbnails: (data: ThumbnailDetails) => void
  setFormData: (data: ListFormDistributionData) => void
  setListData: (data: ListData) => void
  setListStats: (data: statsType) => void
  setStatsLoading: (data: boolean) => void
  openToast: (msg: string, type?: AlertColor, duration?: number) => void
  closeToast: () => void
  clearProfile: () => void
}

export const useStore = create<CartStore>((set, get) => ({
  distributionListDetails: {
    list_id: 0,
    list_name: '',
    status: '',
    description: '',
    updated: null,
    created: null,
    total_contacts: 0,
    active: 0,
    suppressed: 0,
    unsubscribed: 0,
    id: 0,
    full_name: '',
    email: '',
    total: 0,
    type: ''
  },
  listStats: { list_id: null },
  distributionUserDetails: {
    user_details: [
      {
        id: 0,
        full_name: '',
        first_name: '',
        last_name: '',
        email: '',
        status: ''
      }
    ],
    total: 0
  },
  allDistributionUserDetails: {},
  selectedContacts: {
    user_details: [
      {
        id: 0,
        full_name: '',
        first_name: '',
        last_name: '',
        email: '',
        status: ''
      }
    ],
    total: 0
  },
  reviewListDetails: [
    {
      id: 0,
      first_name: '',
      last_name: '',
      email: '',
      status: ''
    }
  ],
  companyThumbnails: [
    {
      id: 0,
      url: '',
      filename: ''
    }
  ],

  wantEdit: false,
  annnoucementPage: 1,
  questionEditModal: false,
  themeLoading: true,
  profileLoading: false,
  showSidebar: true,
  ContentPdf: {
    file: '',
    file_data: {
      name: '',
      size: ''
    }
  },
  permissions: [],
  permissionsLoaded: false,
  UploadFile: '',
  UploadContentUrl: '',
  uploadThread: '',
  redirectionUrl: '',
  annYearsList: ['2024', '2023'],
  statsData: {},
  toastOpen: false,
  toastMessage: '',
  toastType: 'info',
  toastDuration: 2000,
  profile: {
    business_name: '',
    first_name: '',
    last_name: '',
    email: '',
    contact: '',
    user_type: '',
    address_data: {
      city: '',
      state: '',
      street: '',
      postcode: '',
      country: ''
    },
    id: '',
    admin_mail: '',
    service_email: '',
    role_data: {
      name: null,
      id: null
    }
  },
  adminQuestion: {
    id: 0,
    status: '',
    created_on: '',
    answer_date: '',
    email: '',
    business_name: '',
    public: false,
    content: { headline: '', id: 0 },
    user: '',
    first_name: '',
    last_name: '',
    question: '',
    answer: '',
    logo_mark: ''
  },
  initialAdminQuestion: {
    id: 0,
    status: '',
    created_on: '',
    answer_date: '',
    email: '',
    business_name: '',
    public: false,
    content: { headline: '', id: 0 },
    user: '',
    first_name: '',
    last_name: '',
    question: '',
    answer: '',
    logo_mark: ''
  },
  questionTableData: {
    results: [],
    total: 0
  },
  listFormDistributionData: {
    listName: '',
    description: ''
  },
  role: [],
  themeColours: {
    primary: '',
    secondary: '',
    email_bg_color: '#FFFFFF'
  },
  TextColorType: {
    primaryTextDark: true,
    secondaryTextDark: false
  },
  themeLogo: {
    default_logo: 'light',
    logo: 'https://docs.relait.com.au/Ellocent/theme/17119618440545352.png',
    dark_logo: 'https://docs.relait.com.au/Ellocent/theme/17119618440545352.png',
    logo_mark: 'https://docs.relait.com.au/Ellocent/theme/17119618415928862.png',
    dark_logo_mark: 'https://docs.relait.com.au/Ellocent/theme/17119618415928862.png',
    logo_file_name: 'logo.png',
    dark_logo_file_name: 'dark_logo.png',
    logo_mark_file_name: 'logo_mark.png',
    dark_logo_mark_file_name: 'logo_mark.png',
    logo_mark_size: '',
    dark_logo_mark_size: '',
    logo_size: '',
    dark_logo_size: ''
  },
  homeTheme: {
    banner: 'https://docs.relait.com.au/None/theme/1712044609073289.png',
    banner_file_name: 'banner.png',
    login_banner: null,
    title: '',
    text: '',
    code: '',
    notification_stream: false,
    announcement_stream: false,
    primary_domain: '',
    show_new_dashboard: null,
    share_registry: '',
    has_transaction_feed: false,
    enable_placements: false,
    enable_post_ipo: false,
    plan: undefined,
    public_surface: undefined,
    widget_signup: undefined,
    investor_login: undefined,
    corporate_home_url: null,
    business_name: '',
    enable_email_logo: true,
    show_short_summaries: false,
    ps_header: '',
    ps_salutation: '',
    ps_footer: '',
    custom_site_url: '',
    custom_site_announcements_url: '',
    investor_centre_url: '',
    is_custom_site: false,
    hide_from_google: false,
    hide_daily_report: false,
    send_daily_report_email: false,
    news_search_terms: [],
    home_link_behaviour: 'new_tab',
    youtube_sync_enabled: false,
    youtube_channel_url: '',
    youtube_source_title: null,
    youtube_auto_publish: false,
    youtube_backfill_count: 0,
    youtube_last_synced_at: null,
    auto_show_latest_videos: false
  },
  listData: {
    description: '',
    listName: ''
  },
  statsLoading: false,

  fetchPermissions: async () => {
    try {
      const response = await CommonApi({
        path: 'user/permissions',
        method: 'get'
      })
      if (response) {
        set({ permissions: response?.data })
      }
    } catch (error) {
      console.error('error', error)
    } finally {
      set({ permissionsLoaded: true })
    }
  },

  setProfileLoading: (data: any) => {
    set({ profileLoading: data })
  },
  setShowSidebar: (data: boolean) => {
    set({ showSidebar: data })
  },

  setProfile: (data: any) => {
    set({ profile: data })
  },

  fetchProfile: async () => {
    const { setProfileLoading } = get()
    setProfileLoading(true)
    try {
      const response = await CommonApi({
        path: ProfileEndpoint?.get,
        method: 'get'
      })
      if (response) {
        set({ profile: response?.data })
        Cookies.set('server_user_id', response?.data?.id)
      }
    } catch (error) {
      console.error('err', error)
    }
    setProfileLoading(false)
  },

  setContentPdf: (data: any) => {
    set({ ContentPdf: data })
  },
  setAdminQuestion: (data: any) => {
    set({ adminQuestion: data })
  },
  fetchRole: async () => {
    try {
      const response = await CommonApi({
        path: RolesEndpoints?.list,
        method: 'get'
      })
      if (response) {
        set({ role: response?.data })
      }
    } catch (error) {
      console.error('err', error)
    }
  },
  fetchQuestionById: async id => {
    try {
      const response = await CommonApi({
        path: QuestionAnswerEndpoint.getQuestionById(id),
        method: 'get'
      })
      if (response.status) {
        return response.data
      } else {
        return null
      }
    } catch (error) {
      console.error('err', error)
      return null
    }
  },
  fetchAllQuestion: async (query = '', currPage = 1, pageLimit = 10, status = '') => {
    try {
      const response = await CommonApi({
        path: QuestionAnswerEndpoint.getQuestions(query, currPage, pageLimit, status),
        method: 'get'
      })
      if (response.status) {
        set({ questionTableData: response?.data })
      }
    } catch (error) {
      console.error('err', error)
    }
  },
  setQuestionEditModal: (data: boolean) => {
    set({ questionEditModal: data })
  },
  setInitialQuestionChanges: (data: any) => {
    set({ initialAdminQuestion: data })
  },
  setWantEdit: (data: boolean) => {
    set({ wantEdit: data })
  },
  setThemeColours: (data: ThemeColorType) => {
    set({ themeColours: data })
  },
  setTextColorType: (data: TextColorType) => {
    set({ TextColorType: data })
  },
  setThemeLogos: (data: ThemeLogoTypes) => {
    set({ themeLogo: data })
  },
  setHomeTheme: (data: Partial<HomePageTheme>) => {
    set(state => ({ homeTheme: { ...state.homeTheme, ...data } }))
  },
  setUploadFile: data => {
    set({ UploadFile: data })
  },
  setUploadContentUrl: (data: string) => {
    set({ UploadContentUrl: data })
  },
  setUploadThread: data => {
    set({ uploadThread: data })
  },
  setRedirectionUrl: (data: string) => {
    set({ redirectionUrl: data })
  },
  setAnnYearsList: (data: string[]) => {
    set({ annYearsList: data })
  },
  setStatsData: (data: any) => {
    set({ statsData: data })
  },
  setAnnoucementPage: (data: any) => {
    set({ annnoucementPage: data })
  },
  setThemeLoading: (data: any) => {
    set({ themeLoading: data })
  },
  setDistributionListDetails: (data: DistributionListDetails) => {
    set({ distributionListDetails: data })
  },
  setDistributionUserDetails: (data: DistributionUserDetails) => {
    set({ distributionUserDetails: data })
  },
  setAllDistributionUserDetails: (data: AllDistributionUserDetails) => {
    set({ allDistributionUserDetails: data })
  },
  setSelectedContacts: (data: DistributionUserDetails) => {
    set({ selectedContacts: data })
  },
  setReviewListDetails: (data: ReviewListDetails) => {
    set({ reviewListDetails: data })
  },
  setCompanyThumbnails: (data: ThumbnailDetails) => {
    set({ companyThumbnails: data })
  },
  setFormData: (data: ListFormDistributionData) => {
    set({ listFormDistributionData: data })
  },
  setListData: (data: ListData) => {
    set({ listData: data })
  },
  setListStats: (data: statsType) => {
    set({ listStats: data })
  },
  setStatsLoading: (data: boolean) => {
    set({ statsLoading: data })
  },
  openToast: (msg: string, type: AlertColor = 'info', duration: number = 2000) => {
    set({ toastOpen: true, toastMessage: msg, toastType: type, toastDuration: duration })
  },
  closeToast: () => {
    set({ toastOpen: false })
  },
  clearProfile: () => {
    clearAnalyticsFilters()
    Cookies.remove('access_token')
    Cookies.remove('server_user_id')
    set({
      profile: {
        business_name: '',
        first_name: '',
        last_name: '',
        email: '',
        contact: '',
        user_type: '',
        address_data: {
          city: '',
          state: '',
          street: '',
          postcode: '',
          country: ''
        },
        id: '',
        admin_mail: '',
        service_email: '',
        role_data: {
          name: null,
          id: null
        }
      }
    })
  }
}))
